import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

function formatWhen(start) {
  if (!start) return "—";
  // Stored as ISO ("2026-07-02T15:00:00Z" or "2026-07-02T15:00:00+01:00").
  // Render date + time defensively without forcing a timezone shift — the
  // Mordomo persists what the Chefe wrote, we render the same wall-clock.
  const datePart = start.slice(0, 10);
  const timeMatch = start.match(/T(\d{2}:\d{2})/);
  if (!timeMatch) return datePart;
  return `${datePart} · ${timeMatch[1]}`;
}

export default function Appointments() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/appointments", {
        params: { include_inactive: showInactive ? "true" : "false" },
      });
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const { active, cancelled } = useMemo(() => {
    const a = [];
    const c = [];
    for (const ap of items) {
      (ap.active === false ? c : a).push(ap);
    }
    return { active: a, cancelled: c };
  }, [items]);

  return (
    <div
      className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl"
      data-testid="appointments-page"
    >
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-cap">— {t("app.sidebar2.appointments")}</p>
          <h1
            className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3"
            data-testid="appointments-title"
          >
            {t("app.appointments.title")}
          </h1>
          <p className="font-display-italic text-[var(--ink-soft)] mt-3 max-w-3xl">
            {t("app.appointments.sub")}
          </p>
        </div>
        <label
          className="text-xs text-[var(--ink-faint)] tracking-monocle uppercase flex items-center gap-3 cursor-pointer select-none"
          data-testid="appointments-include-inactive-label"
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="accent-[var(--oxblood)]"
            data-testid="appointments-include-inactive"
          />
          {t("app.appointments.includeInactive")}
        </label>
      </div>

      {loading && (
        <p
          className="mt-12 font-display-italic text-[var(--ink-soft)]"
          data-testid="appointments-loading"
        >
          {t("app.appointments.loading")}
        </p>
      )}

      {!loading && error && (
        <p className="text-[var(--error)] text-sm mt-12" data-testid="appointments-error">
          {error}
        </p>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="mt-12 border border-[var(--border)] bg-[var(--bg)] py-12 px-6 text-center"
          data-testid="appointments-empty"
        >
          <p className="font-display-italic text-[var(--ink-soft)] text-lg">
            {t("app.appointments.empty")}
          </p>
          <p className="text-xs text-[var(--ink-faint)] mt-3 tracking-monocle uppercase">
            {t("app.appointments.viaChatHint")}
          </p>
          <Link
            to="/app/chat"
            className="btn-ghost mt-6 inline-block"
            data-testid="appointments-open-chat-empty"
          >
            {t("app.openChat")} →
          </Link>
        </div>
      )}

      {!loading && !error && active.length > 0 && (
        <section className="mt-12" data-testid="appointments-active-section">
          <p className="label-cap text-[var(--oxblood)]">
            {t("app.appointments.activeHeader")} · {active.length}
          </p>
          <ul className="mt-4 space-y-px bg-[var(--border)]" data-testid="appointments-active-list">
            {active.map((ap) => (
              <AppointmentRow key={ap.id} appt={ap} t={t} />
            ))}
          </ul>
        </section>
      )}

      {!loading && !error && cancelled.length > 0 && (
        <section className="mt-12" data-testid="appointments-cancelled-section">
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.appointments.cancelledHeader")} · {cancelled.length}
          </p>
          <ul className="mt-4 space-y-px bg-[var(--border)]" data-testid="appointments-cancelled-list">
            {cancelled.map((ap) => (
              <AppointmentRow key={ap.id} appt={ap} t={t} muted />
            ))}
          </ul>
        </section>
      )}

      {!loading && !error && items.length > 0 && (
        <p
          className="mt-12 text-xs text-[var(--ink-faint)] tracking-monocle uppercase flex items-center gap-4 flex-wrap"
          data-testid="appointments-via-chat-footer"
        >
          <span>{t("app.appointments.viaChatHint")}</span>
          <Link
            to="/app/chat"
            className="underline-offset-4 hover:underline text-[var(--ink-soft)]"
            data-testid="appointments-open-chat-footer"
          >
            {t("app.openChat")} →
          </Link>
        </p>
      )}
    </div>
  );
}

function AppointmentRow({ appt, t, muted = false }) {
  return (
    <article
      className={`bg-[var(--bg)] p-6 grid grid-cols-12 gap-4 items-baseline ${
        muted ? "opacity-60" : ""
      }`}
      data-testid={`appointment-${appt.id}`}
    >
      <div className="col-span-12 md:col-span-5">
        <p
          className={`font-display text-2xl leading-snug ${
            muted ? "line-through" : ""
          }`}
          data-testid={`appointment-title-${appt.id}`}
        >
          {appt.title}
        </p>
        {appt.specialty && (
          <p
            className="text-sm text-[var(--ink-soft)] mt-1"
            data-testid={`appointment-specialty-${appt.id}`}
          >
            {appt.specialty}
          </p>
        )}
      </div>
      <div className="col-span-12 md:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div data-testid={`appointment-when-${appt.id}`}>
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.appointments.whenLabel")}
          </p>
          <p className="font-display text-lg mt-1">{formatWhen(appt.start)}</p>
        </div>
        <div data-testid={`appointment-location-${appt.id}`}>
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.appointments.locationLabel")}
          </p>
          <p className="font-display text-lg mt-1">
            {appt.location || "—"}
          </p>
        </div>
        <div data-testid={`appointment-provider-${appt.id}`}>
          <p className="label-cap text-[var(--ink-faint)]">
            {t("app.appointments.providerLabel")}
          </p>
          <p className="font-display text-lg mt-1">
            {appt.provider || "—"}
          </p>
        </div>
        {appt.notes && (
          <p
            className="col-span-1 md:col-span-3 text-sm text-[var(--ink-soft)] mt-1 leading-relaxed"
            data-testid={`appointment-notes-${appt.id}`}
          >
            {appt.notes}
          </p>
        )}
      </div>
    </article>
  );
}
